import { useMemo, useState } from "react";
import { type LoginCallDTO } from "~/features/cms/types/dto";
import { Card } from "~/features/configurator/Linear/components/Card";
import classNames from "classnames";
import { MediasLogo, type MediasVariant } from "~/features/navigation/components/TopBar/MediasLogo";
import { Button, Divider, styled } from "~/common/mui";
import { LinkButton } from "~/common/components/Link";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { REGISTER_PAGE } from "~/utils/url/staticUrls";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { isForcedLogin } from "~/features/auth/login/loginUtils";
import LoginPopup from "~/features/auth/login/components/LoginPopup";
import ResetPasswordPopup from "~/features/auth/login/components/ResetPasswordPopup";
import { createMarkup } from "~/features/cms/utils/markup";
import { userIsLoggedIn } from "~/domains/user/utils/roles";
import { useTranslation } from "react-i18next";
import PersonIcon from "@mui/icons-material/Person";

export interface CMSLoginCallProps extends Omit<LoginCallDTO, "type"> {
  className?: string;
}

export const CMSLoginCall = (props: CMSLoginCallProps) => {
  const mediasLogoVariant: MediasVariant = props.accessLevel.toLowerCase() as MediasVariant;
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();

  const { val: loginPopupOpen, setTrue: openLogin, setFalse: closeLogin } = useBooleanState(false);
  const { val: resetPopupOpen, setTrue: openReset, setFalse: closeReset } = useBooleanState(false);
  const [email, setEmail] = useState("");

  const onResetClick = (email: string) => {
    setEmail(email);
    closeLogin();
    openReset();
  };

  const content = useMemo(() => createMarkup(props.content), [props.content]);

  if (userIsLoggedIn()) {
    return null;
  }

  return (
    <LoginCallCard className={classNames(props.className, "scha__login-call")} disableHover={true}>
      <LoginCallLogo variant={mediasLogoVariant} sx={{ width: "unset", height: "unset" }} />
      <Divider orientation={isDesktop ? "vertical" : "horizontal"} flexItem={true} />
      <LoginCallContent dangerouslySetInnerHTML={content} />
      <LoginCallActions>
        <RegisterButton data-test-id={"register-free"} href={REGISTER_PAGE} size={"medium"} startIcon={<PersonIcon />}>
          {t("page.registerFree.register")}
        </RegisterButton>
        <OrText>{t("common.or")} </OrText>
        <LinkButton data-test-id={"sign-in"} onClick={openLogin}>
          {t("header.link.signIn")}
        </LinkButton>
      </LoginCallActions>
      {(loginPopupOpen || (isForcedLogin() && !resetPopupOpen)) && (
        <LoginPopup initialEmail={""} onResetClick={onResetClick} close={closeLogin} />
      )}
      {resetPopupOpen && <ResetPasswordPopup initialEmail={email} close={closeReset} />}
    </LoginCallCard>
  );
};

const LoginCallCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3, 1.5),
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.up("desktop-s")]: {
    padding: theme.spacing(2),
    flexDirection: "row",
    alignItems: "center",
  },
  margin: "0 auto",
}));

const LoginCallLogo = styled(MediasLogo)(({ theme }) => ({
  width: 145,
  height: "auto",
  alignSelf: "center",
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("desktop-s")]: {
    marginBottom: 0,
    marginRight: theme.spacing(2),
  },
  '&[data-test-variant="general"]': {
    width: "auto",
    height: 22,
    [theme.breakpoints.up("desktop-s")]: {
      marginBottom: 0,
      marginRight: theme.spacing(9.125),
    },
  },
  '&[data-test-variant="plusbusiness"]': {
    height: 44,
  },
  '&[data-test-variant="plus-business"]': {
    height: 44,
  },
}));

const LoginCallContent = styled("div")(({ theme }) => ({
  margin: theme.spacing(2, 0),
  [theme.breakpoints.up("desktop-s")]: {
    margin: theme.spacing(0, 2),
  },
}));

const LoginCallActions = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "center",
  [theme.breakpoints.up("desktop-s")]: {
    flexDirection: "row",
    flex: "none",
    marginLeft: "auto",
  },
  "> *": {
    marginTop: theme.spacing(2),
    [theme.breakpoints.up("desktop-s")]: {
      marginTop: 0,
      marginLeft: theme.spacing(1),
    },
  },
}));

const OrText = styled("span")({
  fontStyle: "italic",
  whiteSpace: "pre",
  marginTop: 0,
});

const RegisterButton = styled(Button)(({ theme }) => ({
  width: "100%",
  [theme.breakpoints.up("desktop-s")]: {
    width: "auto",
  },
}));
